import Phaser from 'phaser';
import { EnemySystem } from './EnemySystem';
import { EnemyToken } from '../entities/enemy';
import type { PartnerType } from './shopCatalog';
import {
  PartnerLevel,
  PartnerRuntime,
  PartnerSlotSnapshot,
  ProjectileData,
  assertPartnerLevel,
  createPartnerGraphic,
  distancePointToSegmentSq,
  getHomePosition,
} from './PartnerBehaviors';
import { SynergySystem } from './SynergySystem';
import { EMPTY_RELIC_MODIFIERS, RelicSystem } from './RelicSystem';
import { isMagicPartner, isMartialPartner, isShootingPartner } from './relicCatalog';
import { getTunables } from '../config/tunables';

export type PartnerVisualSnapshot = {
  ref: PartnerShape;
  type: PartnerType;
  level: PartnerLevel;
  x: number;
  y: number;
};

export type ProjectileVisualSnapshot = {
  ref: Phaser.GameObjects.Arc;
  x: number;
  y: number;
  color: number;
  radius: number;
  rotation: number;
};

export interface PartnerSystemModifiers {
  damageMultiplier: number;
  critChanceBonus: number;
}

const PROJECTILE_POOL_SIZE = 90;
const BASE_ATTACK_RANGES: Record<PartnerType, number> = {
  gunner: 170,
  sword: 68,
  mage: 145,
  nurse: 0,
  engineer: 175,
  assassin: 135,
  tank: 42,
  summoner: 165,
};

const PARTNER_LEVEL_MULTIPLIERS: Record<PartnerLevel, { damage: number; cooldown: number; range: number }> = {
  1: { damage: 1, cooldown: 1, range: 1 },
  2: { damage: 1.3, cooldown: 0.87, range: 1.1 },
  3: { damage: 1.7, cooldown: 0.77, range: 1.2 },
  4: { damage: 2.2, cooldown: 0.67, range: 1.35 },
  5: { damage: 3, cooldown: 0.59, range: 1.5 },
};

export class PartnerSystem {
  private readonly partners: PartnerRuntime[] = [];
  private readonly projectiles: Phaser.GameObjects.Group;
  private readonly synergySystem = new SynergySystem();
  private tankBlockCounter = 0;
  private nextSupportRegenAt = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly player: Phaser.GameObjects.Arc,
    private readonly enemySystem: EnemySystem,
    private readonly templateModifiers: PartnerSystemModifiers = { damageMultiplier: 1, critChanceBonus: 0 },
    private readonly relicSystem?: RelicSystem,
  ) {
    this.projectiles = this.scene.add.group({
      maxSize: PROJECTILE_POOL_SIZE,
      runChildUpdate: false,
    });

    this.scene.physics.add.overlap(this.projectiles, this.enemySystem.getEnemies(), (projectileObj, enemyObj) => {
      const projectile = projectileObj as Phaser.GameObjects.Arc;
      const enemy = enemyObj as EnemyToken;
      if (!projectile.active || !enemy.active) {
        return;
      }

      const data = projectile.getData('projectile') as ProjectileData | undefined;
      if (!data) {
        throw new Error('Projectile is missing damage data');
      }

      const killed = this.enemySystem.takeDamage(enemy, this.applyCrit(data.damage, data.sourceType), data.sourceType);
      if (data.explosionRadius && data.explosionDamage) {
        this.areaDamage(enemy.x, enemy.y, data.explosionRadius, data.explosionDamage, 0xffdf8a, data.sourceType);
      }
      if (data.splitOnHit) {
        this.splitProjectile(enemy.x, enemy.y, data);
      }
      this.tryLightningChain(enemy.x, enemy.y, data.sourceType);
      if (killed) {
        this.handleEnemyKilled(enemy.x, enemy.y, this.scene.time.now);
      }

      if (data.pierceRemaining > 0) {
        data.pierceRemaining -= 1;
        projectile.setData('projectile', data);
        return;
      }

      this.deactivateProjectile(projectile);
    });

    this.addPartner('gunner');
  }

  addPartner(type: PartnerType, level: PartnerLevel = 1) {
    const resolvedLevel = assertPartnerLevel(level);
    const home = getHomePosition(
      this.player,
      { type, level: resolvedLevel, graphic: undefined as never, nextActionAt: 0, busyUntil: 0 },
      this.partners.length,
    );
    const graphic = createPartnerGraphic(this.scene, type, home.x, home.y);
    const partner: PartnerRuntime = {
      type,
      level: resolvedLevel,
      graphic,
      nextActionAt: 0,
      busyUntil: 0,
      lastEnemyInRangeAt: 0,
    };
    this.updatePartnerGraphicLevel(partner);
    this.partners.push(partner);
    return this.partners.length - 1;
  }

  removePartnerAt(index: number) {
    if (!Number.isInteger(index) || index < 0 || index >= this.partners.length) {
      throw new Error(`Invalid partner index: ${index}`);
    }

    const [removed] = this.partners.splice(index, 1);
    this.scene.tweens.killTweensOf(removed.graphic);
    removed.graphic.destroy();
  }

  canMergeSlots(firstIndex: number, secondIndex: number) {
    const first = this.partners[firstIndex];
    const second = this.partners[secondIndex];
    return Boolean(
      first &&
        second &&
        firstIndex !== secondIndex &&
        first.type === second.type &&
        first.level === second.level &&
        first.level < 5,
    );
  }

  private fireSwordWave(partner: PartnerRuntime, target: EnemyToken, timeMs: number) {
    this.fireProjectile(partner.graphic.x, partner.graphic.y, target, {
      kind: 'bullet',
      damage: this.applyDamage(9, partner.type),
      speed: this.applyProjectileSpeed(430, partner.type),
      expiresAt: timeMs + 720,
      target,
      sourceType: partner.type,
      pierceRemaining: partner.level >= 4 ? 2 : 1,
    }, 0xffd35a, 5);
  }

  private pullNearbyEnemies(x: number, y: number, radius: number) {
    this.showPulse(x, y, radius, 0xffd35a, 180);
    for (const enemy of this.enemySystem.getActiveEnemies()) {
      const distance = Phaser.Math.Distance.Between(x, y, enemy.x, enemy.y);
      if (distance <= 1 || distance > radius) {
        continue;
      }
      const angle = Phaser.Math.Angle.Between(enemy.x, enemy.y, x, y);
      const pull = Math.min(22, distance * 0.25);
      enemy.x += Math.cos(angle) * pull;
      enemy.y += Math.sin(angle) * pull;
    }
  }

  private fireEngineerLaser(partner: PartnerRuntime, target: EnemyToken) {
    if (partner.turretX === undefined || partner.turretY === undefined) {
      return;
    }
    const laser = this.scene.add.line(0, 0, partner.turretX, partner.turretY, target.x, target.y, 0xffb052, 0.65).setOrigin(0, 0);
    laser.setLineWidth(3, 1);
    laser.setDepth(23);
    const damage = this.applyDamage(2.4, partner.type);
    for (const enemy of this.enemySystem.getActiveEnemies()) {
      const distSq = distancePointToSegmentSq(enemy.x, enemy.y, partner.turretX, partner.turretY, target.x, target.y);
      if (distSq <= 13 * 13) {
        const killed = this.enemySystem.takeDamage(enemy, damage, partner.type);
        if (killed) {
          this.handleEnemyKilled(enemy.x, enemy.y, this.scene.time.now);
        }
      }
    }
    this.scene.tweens.add({
      targets: laser,
      alpha: 0,
      duration: 120,
      onComplete: () => laser.destroy(),
    });
  }

  private slowEnemiesAround(x: number, y: number, radius: number, amount: number, durationMs: number) {
    this.showPulse(x, y, radius, 0x92c8ff, 240);
    for (const enemy of this.enemySystem.getActiveEnemies()) {
      if (Phaser.Math.Distance.Between(x, y, enemy.x, enemy.y) <= radius + enemy.radius) {
        enemy.setData('slowUntil', this.scene.time.now + durationMs);
        enemy.setData('slowAmount', amount);
      }
    }
  }

  private castMeteorRain(x: number, y: number, timeMs: number) {
    for (let index = 0; index < 3; index += 1) {
      const angle = (Math.PI * 2 * index) / 3 + timeMs * 0.001;
      const radius = 34 + index * 18;
      const hitX = Phaser.Math.Clamp(x + Math.cos(angle) * radius, 20, 370);
      const hitY = Phaser.Math.Clamp(y + Math.sin(angle) * radius, 96, 824);
      this.scene.time.delayedCall(index * 120, () => {
        this.areaDamage(hitX, hitY, this.applyRange(38, 'mage'), this.applyDamage(8, 'mage'), 0xf2a7ff, 'mage');
      });
    }
  }

  private applyTeamDamageBuff(amount: number, expiresAt: number) {
    for (const partner of this.partners) {
      partner.buffDamageBonus = Math.max(partner.buffDamageBonus ?? 0, amount);
      partner.buffUntil = Math.max(partner.buffUntil ?? 0, expiresAt);
    }
    this.showPulse(this.player.x, this.player.y, 58, 0x61d88f, 300);
  }

  findMergePair(type: PartnerType, level?: PartnerLevel) {
    for (let firstIndex = 0; firstIndex < this.partners.length; firstIndex += 1) {
      const first = this.partners[firstIndex];
      if (first.type !== type || first.level >= 5 || (level !== undefined && first.level !== level)) {
        continue;
      }
      for (let secondIndex = firstIndex + 1; secondIndex < this.partners.length; secondIndex += 1) {
        const second = this.partners[secondIndex];
        if (second.type === first.type && second.level === first.level) {
          return { firstIndex, secondIndex, type: first.type, level: first.level };
        }
      }
    }
    return undefined;
  }

  findAutoMergeTarget(type: PartnerType) {
    const counts = new Map<PartnerLevel, number>();
    for (const partner of this.partners) {
      if (partner.type === type && partner.level < 5) {
        counts.set(partner.level, (counts.get(partner.level) ?? 0) + 1);
      }
    }

    for (let level = 1; level <= 4; level += 1) {
      const typedLevel = level as PartnerLevel;
      if ((counts.get(typedLevel) ?? 0) >= 1) {
        return typedLevel;
      }
    }
    return undefined;
  }

  mergeSlots(firstIndex: number, secondIndex: number) {
    if (!this.canMergeSlots(firstIndex, secondIndex)) {
      throw new Error(`Cannot merge partner slots ${firstIndex} and ${secondIndex}`);
    }

    const keepIndex = Math.min(firstIndex, secondIndex);
    const removeIndex = Math.max(firstIndex, secondIndex);
    const keeper = this.partners[keepIndex];
    const removed = this.partners[removeIndex];
    const nextLevel = assertPartnerLevel(keeper.level + 1);

    this.scene.tweens.killTweensOf(removed.graphic);
    this.partners.splice(removeIndex, 1);
    removed.graphic.destroy();

    keeper.level = nextLevel;
    keeper.nextActionAt = 0;
    keeper.busyUntil = 0;
    keeper.actionCounter = 0;
    this.updatePartnerGraphicLevel(keeper);
    this.showLevelUpPulse(keeper);
    return { index: keepIndex, type: keeper.type, level: keeper.level };
  }

  mergeFirstPair(type: PartnerType, level?: PartnerLevel) {
    const pair = this.findMergePair(type, level);
    if (!pair) {
      return undefined;
    }
    return this.mergeSlots(pair.firstIndex, pair.secondIndex);
  }

  update(timeMs: number) {
    this.relicSystem?.update(timeMs);
    this.updateProjectiles(timeMs);
    this.updateSupportRegen(timeMs);

    for (let index = 0; index < this.partners.length; index += 1) {
      const partner = this.partners[index];
      this.updatePartnerPosition(partner, index, timeMs);
      this.updatePartnerAction(partner, index, timeMs);
    }
  }

  tryTankBlock() {
    const hasTank = this.partners.some((partner) => partner.type === 'tank');
    if (!hasTank) {
      return false;
    }

    this.tankBlockCounter += 1;
    const threshold = this.getRelicModifiers().tankBlockFrequencyBonus > 0 ? 2 : 3;
    if (this.tankBlockCounter % threshold !== 0) {
      return false;
    }

    this.showTankBlock();
    this.scene.events.emit('partner-tank-block');
    return true;
  }

  getPartnerTypes() {
    return this.partners.map((partner) => partner.type);
  }

  getPartnerSlots(): PartnerSlotSnapshot[] {
    return this.partners.map((partner) => ({ type: partner.type, level: partner.level }));
  }

  getPartnerVisuals(): PartnerVisualSnapshot[] {
    return this.partners.map((partner) => ({
      ref: partner.graphic,
      type: partner.type,
      level: partner.level,
      x: partner.graphic.x,
      y: partner.graphic.y,
    }));
  }

  getProjectileVisuals(): ProjectileVisualSnapshot[] {
    return this.projectiles
      .getChildren()
      .map((child) => child as Phaser.GameObjects.Arc)
      .filter((projectile) => projectile.active)
      .map((projectile) => ({
        ref: projectile,
        x: projectile.x,
        y: projectile.y,
        color: projectile.fillColor,
        radius: projectile.radius,
        rotation: projectile.rotation,
      }));
  }

  getSynergyState() {
    return this.synergySystem.calculate(this.getPartnerTypes());
  }

  getDamageTakenMultiplier() {
    const relicModifiers = this.getRelicModifiers();
    const hasLevelFiveTank = this.partners.some((partner) => partner.type === 'tank' && partner.level >= 5);
    return Math.max(
      0.15,
      1 +
        relicModifiers.damageTakenBonus -
        this.getSynergyModifiers().supportDamageTakenReduction -
        relicModifiers.supportDamageTakenReduction -
        (hasLevelFiveTank ? 0.25 : 0),
    );
  }

  private updatePartnerPosition(partner: PartnerRuntime, index: number, timeMs: number) {
    if (partner.type === 'engineer') {
      if (partner.turretX === undefined || partner.turretY === undefined) {
        this.deployTurret(partner);
      }
      partner.graphic.setPosition(partner.turretX ?? this.player.x, partner.turretY ?? this.player.y);
      return;
    }

    if (timeMs < partner.busyUntil) {
      return;
    }

    const home = getHomePosition(this.player, partner, index);
    partner.graphic.setPosition(home.x, home.y);
  }

  private updatePartnerGraphicLevel(partner: PartnerRuntime) {
    const scale = 1 + (partner.level - 1) * 0.09;
    partner.graphic.setScale(scale);
    const strokeAlpha = partner.level >= 5 ? 1 : 0.78;
    const strokeColor = partner.level >= 5 ? 0xfff0a8 : 0xffffff;
    partner.graphic.setStrokeStyle(partner.level >= 5 ? 3 : 2, strokeColor, strokeAlpha);
    partner.graphic.setData('partnerLevel', partner.level);
  }

  private showLevelUpPulse(partner: PartnerRuntime) {
    this.showPulse(partner.graphic.x, partner.graphic.y, 30 + partner.level * 8, 0xfff0a8, 360);
    const text = this.scene.add.text(partner.graphic.x, partner.graphic.y - 30, `Lv.${partner.level}`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      color: '#fff0a8',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(60);
    this.scene.tweens.add({
      targets: text,
      y: text.y - 18,
      alpha: 0,
      duration: 520,
      onComplete: () => text.destroy(),
    });
  }

  private updatePartnerAction(partner: PartnerRuntime, _index: number, timeMs: number) {
    if (timeMs < partner.nextActionAt || timeMs < partner.busyUntil) {
      return;
    }

    if (partner.type === 'gunner') {
      const target = this.getNearestEnemyInRange(
        partner.graphic.x,
        partner.graphic.y,
        this.applyRange(BASE_ATTACK_RANGES.gunner, partner.type),
      );
      if (target) {
        this.fireGunner(partner, target, timeMs);
        partner.nextActionAt = timeMs + this.applyCooldown(350, partner.type);
      }
      return;
    }

    if (partner.type === 'sword') {
      const target = this.getNearestEnemyInRange(
        this.player.x,
        this.player.y,
        this.applyRange(BASE_ATTACK_RANGES.sword + 44, partner.type),
      );
      if (target) {
        const guardMode = this.countEnemiesNear(this.player.x, this.player.y, 72) >= 3;
        const martialLifestealBonus = this.getSynergyModifiers().martialLifestealBonus;
        this.lungeSlash(
          partner,
          target,
          this.applyDamage((partner.level >= 5 ? 27 : 18) * (guardMode ? 1.18 : 1), partner.type),
          this.applyRange(BASE_ATTACK_RANGES.sword + (guardMode ? 14 : 0), partner.type),
          0xffd35a,
          timeMs,
          guardMode ? 300 : 340,
          (guardMode ? 3 : 2) + martialLifestealBonus,
          (guardMode ? 10 : 6) + martialLifestealBonus * 3,
          guardMode ? 34 : 28,
        );
        if (partner.level >= 3) {
          this.fireSwordWave(partner, target, timeMs);
        }
        if (partner.level >= 5) {
          this.ringDamage(
            partner.graphic.x,
            partner.graphic.y,
            this.applyRange(92, partner.type),
            this.applyDamage(12, partner.type),
            0xffd35a,
            true,
            0,
            'sword',
          );
        }
        partner.nextActionAt = timeMs + this.applyCooldown(430, partner.type);
      }
      return;
    }

    if (partner.type === 'mage') {
      const target = this.getNearestEnemyInRange(
        this.player.x,
        this.player.y,
        this.applyRange(BASE_ATTACK_RANGES.mage, partner.type),
      );
      if (target) {
        this.areaDamage(target.x, target.y, this.applyRange(44, partner.type), this.applyDamage(10, partner.type), 0xb56cff);
        if (partner.level >= 3) {
          this.areaDamage(target.x + 28, target.y - 20, this.applyRange(34, partner.type), this.applyDamage(7, partner.type), 0xdbc0ff);
        }
        if (partner.level >= 4) {
          this.slowEnemiesAround(target.x, target.y, this.applyRange(70, partner.type), 0.5, 2000);
        }
        partner.actionCounter = (partner.actionCounter ?? 0) + 1;
        if (partner.level >= 5 && partner.actionCounter % 3 === 0) {
          this.castMeteorRain(target.x, target.y, timeMs);
        }
        partner.nextActionAt = timeMs + this.applyCooldown(800, partner.type);
      }
      return;
    }

    if (partner.type === 'nurse') {
      this.scene.events.emit('partner-heal', this.applySupportValue(5));
      this.showPulse(partner.graphic.x, partner.graphic.y, 42, 0x61d88f, 260);
      if (partner.level >= 3) {
        this.scene.events.emit('partner-shield', partner.level >= 5 ? 2 : 1);
      }
      if (partner.level >= 4) {
        this.applyTeamDamageBuff(0.15, timeMs + 4000);
      }
      partner.nextActionAt = timeMs + this.applyCooldown(1600, partner.type);
      return;
    }

    if (partner.type === 'engineer') {
      this.updateEngineer(partner, timeMs);
      return;
    }

    if (partner.type === 'assassin') {
      const target = this.getNearestEnemyInRange(
        this.player.x,
        this.player.y,
        this.applyRange(BASE_ATTACK_RANGES.assassin, partner.type),
      );
      if (target) {
        this.piercingLunge(partner, target, timeMs);
        partner.nextActionAt = timeMs + this.applyCooldown(760, partner.type);
      }
      return;
    }

    if (partner.type === 'tank') {
      this.ringDamage(
        partner.graphic.x,
        partner.graphic.y,
        this.applyRange(BASE_ATTACK_RANGES.tank, partner.type),
        this.applyDamage(8, partner.type),
        0x8aa0a8,
        true,
        partner.level >= 5 ? 1000 : 0,
      );
      partner.nextActionAt = timeMs + this.applyCooldown(800, partner.type);
      return;
    }

    if (partner.type === 'summoner') {
      const target = this.getNearestEnemyInRange(
        partner.graphic.x,
        partner.graphic.y,
        this.applyRange(BASE_ATTACK_RANGES.summoner, partner.type),
      );
      if (target) {
        const shots = partner.level >= 2 ? 2 : 1;
        for (let shot = 0; shot < shots; shot += 1) {
          this.fireProjectile(partner.graphic.x, partner.graphic.y, target, {
          kind: 'homing',
          damage: this.applyDamage(7, partner.type),
          speed: this.applyProjectileSpeed(260, partner.type),
          expiresAt: timeMs + 2200,
          target,
          sourceType: partner.type,
          pierceRemaining: partner.level >= 5 ? 1 : 0,
          splitOnHit: partner.level >= 5,
        }, 0x5bd5d1, 5);
        }
        partner.nextActionAt = timeMs + this.applyCooldown(600, partner.type);
      }
    }
  }

  private updateEngineer(partner: PartnerRuntime, timeMs: number) {
    if (partner.turretX === undefined || partner.turretY === undefined) {
      this.deployTurret(partner);
      return;
    }

    const target = this.getNearestEnemyInRange(
      partner.turretX,
      partner.turretY,
      this.applyRange(BASE_ATTACK_RANGES.engineer, partner.type),
    );
    if (target) {
      partner.lastEnemyInRangeAt = timeMs;
      const shots = partner.level >= 2 ? 2 : 1;
      const offsets = shots === 1 ? [0] : [-0.12, 0.12];
      for (const offset of offsets) {
        this.fireProjectile(partner.turretX, partner.turretY, target, {
          kind: partner.level >= 5 ? 'homing' : 'bullet',
          damage: this.applyDamage(6, partner.type),
          speed: this.applyProjectileSpeed(partner.level >= 5 ? 340 : 520, partner.type),
          expiresAt: timeMs + (partner.level >= 5 ? 2200 : 1800),
          target,
          sourceType: partner.type,
          pierceRemaining: this.getProjectilePierce(partner.type),
          explosionRadius: partner.level >= 3 ? this.applyRange(partner.level >= 5 ? 34 : 22, partner.type) : 0,
          explosionDamage: partner.level >= 3 ? this.applyDamage(partner.level >= 5 ? 5 : 3, partner.type) : 0,
        }, partner.level >= 5 ? 0xff7f3f : 0xffb052, partner.level >= 5 ? 6 : 4, offset);
      }
      if (partner.level >= 4) {
        this.fireEngineerLaser(partner, target);
      }
      partner.nextActionAt = timeMs + this.applyCooldown(500, partner.type);
      return;
    }

    const noTargetMs = timeMs - (partner.lastEnemyInRangeAt ?? 0);
    if (noTargetMs >= 2000 || timeMs >= partner.busyUntil) {
      this.deployTurret(partner);
      partner.nextActionAt = timeMs + 250;
    } else {
      partner.nextActionAt = timeMs + 250;
    }
  }

  private deployTurret(partner: PartnerRuntime) {
    partner.turretX = this.player.x;
    partner.turretY = this.player.y;
    partner.lastEnemyInRangeAt = this.scene.time.now;
    partner.busyUntil = this.scene.time.now + 8000;
    partner.graphic.setPosition(partner.turretX, partner.turretY);
    partner.graphic.setScale(1.25);

    this.scene.tweens.add({
      targets: partner.graphic,
      scale: 1,
      duration: 180,
    });
  }

  private fireProjectile(
    x: number,
    y: number,
    target: EnemyToken,
    data: ProjectileData,
    color: number,
    radius: number,
    angleOffset = 0,
  ) {
    const projectile = this.getProjectile(color, radius);
    if (!projectile) {
      return;
    }
    projectile.setPosition(x, y);
    projectile.setData('projectile', data);
    projectile.setActive(true).setVisible(true);
    projectile.setDepth(24);

    const body = projectile.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setCircle(radius);
    body.setOffset(-radius, -radius);
    if (angleOffset === 0) {
      this.scene.physics.moveToObject(projectile, target, data.speed);
    } else {
      const angle = Phaser.Math.Angle.Between(x, y, target.x, target.y) + angleOffset;
      body.setVelocity(Math.cos(angle) * data.speed, Math.sin(angle) * data.speed);
    }
  }

  private fireGunner(partner: PartnerRuntime, target: EnemyToken, timeMs: number) {
    const shots = partner.level >= 3 ? 2 : 1;
    const offsets = shots === 1 ? [0] : [-0.13, 0.13];
    const burstCount = partner.level >= 5 ? 3 : 1;
    for (let burst = 0; burst < burstCount; burst += 1) {
      for (const offset of offsets) {
        this.scene.time.delayedCall(burst * 70, () => {
          if (!partner.graphic.active || !target.active) {
            return;
          }
          this.fireProjectile(partner.graphic.x, partner.graphic.y, target, {
            kind: 'bullet',
            damage: this.applyDamage(partner.level >= 5 ? 7 : 8, partner.type),
            speed: this.applyProjectileSpeed(600, partner.type),
            expiresAt: timeMs + 1900 + burst * 70,
            target,
            sourceType: partner.type,
            pierceRemaining: this.getProjectilePierce(partner.type) + (partner.level >= 5 ? 2 : partner.level >= 2 ? 1 : 0),
            splitOnHit: partner.level >= 4,
            explosionRadius: partner.level >= 5 ? 20 : 0,
            explosionDamage: partner.level >= 5 ? this.applyDamage(4, partner.type) : 0,
          }, 0xffffff, 4, offset);
        });
      }
    }
  }

  private splitProjectile(x: number, y: number, data: ProjectileData) {
    const target = this.enemySystem.getNearestEnemy(x, y);
    const baseAngle = target ? Phaser.Math.Angle.Between(x, y, target.x, target.y) : -Math.PI / 2;
    for (const offset of [-Math.PI / 4, Math.PI / 4]) {
      const dummyTarget = {
        x: x + Math.cos(baseAngle + offset) * 100,
        y: y + Math.sin(baseAngle + offset) * 100,
      } as EnemyToken;
      this.fireProjectile(x, y, dummyTarget, {
        kind: 'bullet',
        damage: data.damage * 0.45,
        speed: data.speed * 0.86,
        expiresAt: this.scene.time.now + 620,
        sourceType: data.sourceType,
        pierceRemaining: 0,
      }, 0xffdf8a, 3);
    }
  }

  private getProjectile(color: number, radius: number) {
    let projectile = this.projectiles.getFirstDead(false) as Phaser.GameObjects.Arc | null;
    if (!projectile) {
      if (this.projectiles.getLength() >= PROJECTILE_POOL_SIZE) {
        return null;
      }
      projectile = this.scene.add.circle(0, 0, radius, color, 1);
      this.scene.physics.add.existing(projectile);
      this.projectiles.add(projectile);
    }

    projectile.setRadius(radius);
    projectile.setFillStyle(color, 1);
    return projectile;
  }

  private updateProjectiles(timeMs: number) {
    for (const child of this.projectiles.getChildren()) {
      const projectile = child as Phaser.GameObjects.Arc;
      if (!projectile.active) {
        continue;
      }

      const data = projectile.getData('projectile') as ProjectileData | undefined;
      if (!data) {
        throw new Error('Active projectile is missing data');
      }

      if (timeMs >= data.expiresAt) {
        this.deactivateProjectile(projectile);
        continue;
      }

      if (data.kind === 'homing') {
        const target = data.target?.active ? data.target : this.enemySystem.getNearestEnemy(projectile.x, projectile.y);
        if (target) {
          data.target = target;
          this.scene.physics.moveToObject(projectile, target, data.speed);
          projectile.setRotation(Phaser.Math.Angle.Between(projectile.x, projectile.y, target.x, target.y));
        }
      }
    }
  }

  private deactivateProjectile(projectile: Phaser.GameObjects.Arc) {
    projectile.setActive(false).setVisible(false);
    projectile.setData('projectile', undefined);
    const body = projectile.body as Phaser.Physics.Arcade.Body;
    body.enable = false;
    body.setVelocity(0, 0);
  }

  private lungeSlash(
    partner: PartnerRuntime,
    target: EnemyToken,
    damage: number,
    range: number,
    color: number,
    timeMs: number,
    durationMs: number,
    lifestealPerHit = 0,
    maxLifesteal = 0,
    knockbackDistance = 18,
  ) {
    const startX = partner.graphic.x;
    const startY = partner.graphic.y;
    const angle = Phaser.Math.Angle.Between(startX, startY, target.x, target.y);
    const lungeDistance = Math.min(76, Phaser.Math.Distance.Between(startX, startY, target.x, target.y));
    const hitX = startX + Math.cos(angle) * lungeDistance;
    const hitY = startY + Math.sin(angle) * lungeDistance;

    partner.busyUntil = timeMs + durationMs;
    this.scene.tweens.killTweensOf(partner.graphic);
    this.scene.tweens.add({
      targets: partner.graphic,
      x: hitX,
      y: hitY,
      duration: durationMs * 0.45,
      yoyo: true,
      ease: 'Sine.easeOut',
    });

    this.showSlash(hitX, hitY, angle, range, color);

    const halfAngle = Math.PI / 2;
    let hitCount = 0;
    for (const enemy of this.enemySystem.getActiveEnemies()) {
      const distance = Phaser.Math.Distance.Between(hitX, hitY, enemy.x, enemy.y);
      if (distance > range) {
        continue;
      }

      const enemyAngle = Phaser.Math.Angle.Between(startX, startY, enemy.x, enemy.y);
      const delta = Math.abs(Phaser.Math.Angle.Wrap(enemyAngle - angle));
      if (delta <= halfAngle) {
        hitCount += 1;
        const killed = this.enemySystem.takeDamage(enemy, damage, 'sword');
        if (killed) {
          this.handleEnemyKilled(enemy.x, enemy.y, timeMs);
        } else if (enemy.active) {
          const pushAngle = Phaser.Math.Angle.Between(hitX, hitY, enemy.x, enemy.y);
          const pushDistance = knockbackDistance * (1 + this.getSynergyModifiers().martialKnockbackBonus);
          enemy.x += Math.cos(pushAngle) * pushDistance;
          enemy.y += Math.sin(pushAngle) * pushDistance;
        }
      }
    }

    if (hitCount > 0 && lifestealPerHit > 0 && maxLifesteal > 0) {
      this.scene.events.emit('partner-heal', Math.min(maxLifesteal, hitCount * lifestealPerHit));
    }
  }

  private countEnemiesNear(x: number, y: number, radius: number) {
    let count = 0;
    for (const enemy of this.enemySystem.getActiveEnemies()) {
      if (Phaser.Math.Distance.Between(x, y, enemy.x, enemy.y) <= radius + enemy.radius) {
        count += 1;
      }
    }
    return count;
  }

  private piercingLunge(partner: PartnerRuntime, target: EnemyToken, timeMs: number) {
    const startX = partner.graphic.x;
    const startY = partner.graphic.y;
    const angle = Phaser.Math.Angle.Between(startX, startY, target.x, target.y);
    const lungeRange = this.applyRange(BASE_ATTACK_RANGES.assassin, partner.type);
    const endX = startX + Math.cos(angle) * lungeRange;
    const endY = startY + Math.sin(angle) * lungeRange;
    const levelCritBonus = partner.level >= 2 ? 0.2 : 0;
    const shadowBonus = partner.level >= 3 && timeMs % 10 < 3 ? 0.55 : 0;
    const damage = this.applyDamage(timeMs % 10 < 3 ? 34 : 22, partner.type) * (1 + levelCritBonus + shadowBonus);

    partner.busyUntil = timeMs + 360;
    this.scene.tweens.killTweensOf(partner.graphic);
    this.scene.tweens.add({
      targets: partner.graphic,
      x: endX,
      y: endY,
      duration: 160,
      yoyo: true,
      ease: 'Cubic.easeOut',
    });

    const trail = this.scene.add.line(0, 0, startX, startY, endX, endY, 0xff5d73, 0.78).setOrigin(0, 0);
    trail.setLineWidth(5, 2);
    trail.setDepth(23);
    this.scene.tweens.add({
      targets: trail,
      alpha: 0,
      duration: 260,
      onComplete: () => trail.destroy(),
    });

    for (const enemy of this.enemySystem.getActiveEnemies()) {
      const distSq = distancePointToSegmentSq(enemy.x, enemy.y, startX, startY, endX, endY);
      if (distSq <= 18 * 18) {
        const execute = partner.level >= 5 && this.isEnemyLowHp(enemy) && Math.abs(Math.floor(timeMs + enemy.x + enemy.y)) % 10 === 0;
        const killed = this.enemySystem.takeDamage(enemy, execute ? enemy.hp : this.applyCrit(damage, partner.type), partner.type);
        if (killed) {
          this.handleEnemyKilled(enemy.x, enemy.y, timeMs);
        }
      }
    }
  }

  private areaDamage(x: number, y: number, radius: number, damage: number, color: number, sourceType: PartnerType = 'mage') {
    this.showPulse(x, y, radius, color, 300);

    for (const enemy of this.enemySystem.getActiveEnemies()) {
      if (Phaser.Math.Distance.Between(x, y, enemy.x, enemy.y) <= radius + enemy.radius) {
        const killed = this.enemySystem.takeDamage(enemy, this.applyCrit(damage, sourceType), sourceType);
        if (killed) {
          this.handleEnemyKilled(enemy.x, enemy.y, this.scene.time.now);
        }
      }
    }
  }

  private ringDamage(
    x: number,
    y: number,
    radius: number,
    damage: number,
    color: number,
    pushback: boolean,
    stunMs = 0,
    sourceType: PartnerType = 'tank',
  ) {
    this.showPulse(x, y, radius, color, 320);

    for (const enemy of this.enemySystem.getActiveEnemies()) {
      if (Phaser.Math.Distance.Between(x, y, enemy.x, enemy.y) > radius + enemy.radius) {
        continue;
      }

      const killed = this.enemySystem.takeDamage(enemy, this.applyCrit(damage, sourceType), sourceType);
      if (killed) {
        this.handleEnemyKilled(enemy.x, enemy.y, this.scene.time.now);
      }
      if (pushback && enemy.active) {
        const angle = Phaser.Math.Angle.Between(x, y, enemy.x, enemy.y);
        const pushDistance = 16 * (1 + this.getSynergyModifiers().martialKnockbackBonus);
        enemy.x += Math.cos(angle) * pushDistance;
        enemy.y += Math.sin(angle) * pushDistance;
      }
      if (stunMs > 0 && enemy.active) {
        enemy.setData('stunnedUntil', this.scene.time.now + stunMs);
      }
    }
  }

  private showSlash(x: number, y: number, angle: number, range: number, color: number) {
    this.scene.events.emit('visual-slash', { x, y, angle, range, color });
    const left = angle - Math.PI / 2;
    const right = angle + Math.PI / 2;
    const slash = this.scene.add.graphics();
    slash.setDepth(23);
    slash.fillStyle(color, 0.34);
    slash.fillTriangle(
      x,
      y,
      x + Math.cos(left) * range,
      y + Math.sin(left) * range,
      x + Math.cos(right) * range,
      y + Math.sin(right) * range,
    );

    this.scene.tweens.add({
      targets: slash,
      alpha: 0,
      duration: 180,
      onComplete: () => slash.destroy(),
    });
  }

  private showPulse(x: number, y: number, radius: number, color: number, durationMs: number) {
    this.scene.events.emit('visual-pulse', { x, y, radius, color, durationMs });
    const pulse = this.scene.add.circle(x, y, radius, color, 0.22);
    pulse.setStrokeStyle(3, color, 0.74);
    pulse.setScale(0.12);
    pulse.setDepth(23);

    this.scene.tweens.add({
      targets: pulse,
      scale: 1,
      alpha: 0,
      duration: durationMs,
      ease: 'Sine.easeOut',
      onComplete: () => pulse.destroy(),
    });
  }

  private showTankBlock() {
    this.showPulse(this.player.x, this.player.y, 46, 0xcfd7d9, 260);
  }

  private getNearestEnemyInRange(x: number, y: number, range: number) {
    let nearest: EnemyToken | null = null;
    let minDistSq = range * range;

    for (const enemy of this.enemySystem.getActiveEnemies()) {
      const distSq = Phaser.Math.Distance.Squared(x, y, enemy.x, enemy.y);
      if (distSq <= minDistSq) {
        minDistSq = distSq;
        nearest = enemy;
      }
    }

    return nearest;
  }

  private getSynergyModifiers() {
    return this.getSynergyState().modifiers;
  }

  private getRelicModifiers() {
    if (!this.relicSystem) {
      return EMPTY_RELIC_MODIFIERS;
    }
    return this.relicSystem.getModifiers({
      wave: this.scene.registry.get('m1.wave') ?? 1,
      materials: this.scene.registry.get('m1.materials') ?? 0,
      hp: this.scene.registry.get('m1.hp') ?? 100,
      maxHp: this.scene.registry.get('m1.maxHp') ?? 100,
      partnerTypes: this.getPartnerTypes(),
    });
  }

  private applyDamage(baseDamage: number, type: PartnerType) {
    const modifiers = this.getSynergyModifiers();
    const relicModifiers = this.getRelicModifiers();
    const level = this.getMaxPartnerLevel(type);
    let multiplier = 1;
    multiplier += relicModifiers.damageBonus;
    if (isMartialPartner(type)) {
      multiplier += modifiers.martialDamageBonus;
      multiplier += relicModifiers.martialDamageBonus;
    }
    if (isMagicPartner(type)) {
      multiplier += modifiers.magicDamageBonus;
      multiplier += relicModifiers.magicDamageBonus;
    }
    if (isShootingPartner(type)) {
      multiplier += relicModifiers.shootingDamageBonus;
    }
    multiplier *= this.getLevelMultiplier(level, 'damage');
    const activeBuff = this.partners.find((candidate) => candidate.type === type && candidate.buffUntil && candidate.buffUntil > this.scene.time.now);
    if (activeBuff) {
      multiplier += activeBuff.buffDamageBonus ?? 0;
    }
    multiplier += this.getUpgradeModifiers().damageBonus;
    return (baseDamage + relicModifiers.flatDamageBonus) * multiplier * this.templateModifiers.damageMultiplier * getTunables().partners.damageMultiplier;
  }

  private applyCrit(damage: number, type: PartnerType) {
    const modifiers = this.getSynergyModifiers();
    const relicModifiers = this.getRelicModifiers();
    const critChance =
      (type === 'gunner' || type === 'engineer' ? modifiers.shootingCritChanceBonus : 0) +
      (type === 'sword' || type === 'assassin' || type === 'tank' ? modifiers.martialCritChanceBonus : 0) +
      relicModifiers.critChanceBonus +
      this.getUpgradeModifiers().critChanceBonus +
      this.templateModifiers.critChanceBonus;

    if (critChance <= 0) {
      return damage;
    }

    const bucket = Math.abs(Math.floor(this.scene.time.now + damage * 17)) % 100;
    return bucket < critChance * 100 ? damage * relicModifiers.critMultiplier : damage;
  }

  private applyRange(baseRange: number, type: PartnerType) {
    const modifiers = this.getSynergyModifiers();
    const relicModifiers = this.getRelicModifiers();
    let multiplier = 1 + this.getUpgradeModifiers().attackRangeBonus;
    if (isMartialPartner(type)) {
      multiplier += modifiers.martialRangeBonus;
      multiplier += relicModifiers.martialRangeBonus;
    }
    if (isMagicPartner(type)) {
      multiplier += modifiers.magicRangeBonus;
      multiplier += relicModifiers.magicRangeBonus;
    }
    return baseRange * multiplier * this.getLevelMultiplier(this.getMaxPartnerLevel(type), 'range') * getTunables().partners.rangeMultiplier;
  }

  private applyCooldown(baseCooldown: number, type: PartnerType) {
    const modifiers = this.getSynergyModifiers();
    const relicModifiers = this.getRelicModifiers();
    let speedBonus = 0;
    speedBonus += relicModifiers.attackSpeedBonus;
    if (type === 'gunner' || type === 'engineer') {
      speedBonus += modifiers.shootingFireRateBonus;
      speedBonus += relicModifiers.shootingFireRateBonus;
    }
    if (type === 'mage' || type === 'summoner') {
      speedBonus += modifiers.magicCooldownBonus;
    }
    if (type === 'nurse' || type === 'engineer') {
      speedBonus += modifiers.supportCooldownBonus;
      speedBonus += relicModifiers.supportCooldownBonus;
    }
    speedBonus += this.getUpgradeModifiers().attackSpeedBonus;
    return (
      baseCooldown *
      this.getLevelMultiplier(this.getMaxPartnerLevel(type), 'cooldown') /
      ((1 + speedBonus) * getTunables().partners.attackSpeedMultiplier)
    );
  }

  private getLevelMultiplier(level: PartnerLevel, stat: 'damage' | 'cooldown' | 'range') {
    return PARTNER_LEVEL_MULTIPLIERS[level][stat];
  }

  private getMaxPartnerLevel(type: PartnerType): PartnerLevel {
    let maxLevel: PartnerLevel = 1;
    for (const partner of this.partners) {
      if (partner.type === type && partner.level > maxLevel) {
        maxLevel = partner.level;
      }
    }
    return maxLevel;
  }

  private getUpgradeModifiers() {
    const modifiers = this.scene.registry.get('m3.upgradeModifiers') ?? {
      damageBonus: 0,
      attackRangeBonus: 0,
      attackSpeedBonus: 0,
      moveSpeedBonus: 0,
      maxHpDelta: 0,
      pickupRangeBonus: 0,
      critChanceBonus: 0,
    };
    if (
      typeof modifiers !== 'object' ||
      modifiers === null ||
      !Number.isFinite(modifiers.damageBonus) ||
      !Number.isFinite(modifiers.attackRangeBonus) ||
      !Number.isFinite(modifiers.attackSpeedBonus) ||
      !Number.isFinite(modifiers.moveSpeedBonus) ||
      !Number.isFinite(modifiers.maxHpDelta) ||
      !Number.isFinite(modifiers.pickupRangeBonus) ||
      !Number.isFinite(modifiers.critChanceBonus)
    ) {
      throw new Error('Invalid upgrade modifiers in registry');
    }
    return modifiers;
  }

  private applyProjectileSpeed(baseSpeed: number, type: PartnerType) {
    const modifiers = this.getSynergyModifiers();
    const relicModifiers = this.getRelicModifiers();
    const speedBonus =
      relicModifiers.projectileSpeedBonus +
      (type === 'gunner' || type === 'engineer'
        ? modifiers.shootingProjectileSpeedBonus + relicModifiers.shootingProjectileSpeedBonus
        : 0);
    return baseSpeed * (1 + speedBonus) * getTunables().partners.projectileSpeedMultiplier;
  }

  private applySupportValue(baseValue: number) {
    const nurseLevel = this.getMaxPartnerLevel('nurse');
    const levelBonus = nurseLevel >= 2 ? 0.3 : 0;
    return (
      baseValue *
      this.getLevelMultiplier(nurseLevel, 'damage') *
      (1 + this.getSynergyModifiers().supportEffectBonus + this.getRelicModifiers().supportEffectBonus + levelBonus)
    );
  }

  private updateSupportRegen(timeMs: number) {
    const regen = this.getSynergyModifiers().supportRegenPerSecond;
    if (regen <= 0 || timeMs < this.nextSupportRegenAt) {
      return;
    }

    this.scene.events.emit('partner-heal', regen);
    this.nextSupportRegenAt = timeMs + 1000;
  }

  private tryMagicKillExplosion(x: number, y: number) {
    const explosionDamage = this.getSynergyModifiers().magicKillExplosionDamage;
    if (explosionDamage <= 0) {
      return;
    }

    this.showPulse(x, y, 36, 0xb56cff, 220);
    for (const enemy of this.enemySystem.getActiveEnemies()) {
      if (Phaser.Math.Distance.Between(x, y, enemy.x, enemy.y) <= 36 + enemy.radius) {
        this.enemySystem.takeDamage(enemy, explosionDamage, 'mage');
      }
    }
  }

  private getProjectilePierce(type: PartnerType) {
    const synergyPierce = this.getSynergyModifiers().shootingPierce;
    const relicPierce = isShootingPartner(type) ? this.getRelicModifiers().shootingPierceBonus : 0;
    return synergyPierce + relicPierce;
  }

  private handleEnemyKilled(x: number, y: number, timeMs: number) {
    this.relicSystem?.recordKill(timeMs);
    const killHeal = this.getRelicModifiers().killHeal + (this.hasPartnerAtLevel('assassin', 5) ? 1 : 0);
    if (killHeal > 0) {
      this.scene.events.emit('partner-heal', killHeal);
    }
    this.tryMagicKillExplosion(x, y);
  }

  private isEnemyLowHp(enemy: EnemyToken) {
    const maxHp = (enemy.getData('maxHp') as number | undefined) ?? Math.max(enemy.hp, 1);
    return enemy.hp <= Math.max(1, maxHp * 0.2);
  }

  private hasPartnerAtLevel(type: PartnerType, level: PartnerLevel) {
    return this.partners.some((partner) => partner.type === type && partner.level >= level);
  }

  private tryLightningChain(x: number, y: number, sourceType: PartnerType) {
    if (!isShootingPartner(sourceType) && sourceType !== 'summoner') {
      return;
    }
    const modifiers = this.getRelicModifiers();
    if (modifiers.lightningChance <= 0 || modifiers.lightningDamage <= 0) {
      return;
    }
    const bucket = Math.abs(Math.floor(this.scene.time.now + x * 3 + y * 5)) % 100;
    if (bucket >= modifiers.lightningChance * 100) {
      return;
    }
    const target = this.getNearestEnemyInRange(x, y, 120);
    if (!target) {
      return;
    }
    this.showPulse(target.x, target.y, 18, 0x8ad8ff, 160);
    const killed = this.enemySystem.takeDamage(target, modifiers.lightningDamage, sourceType);
    if (killed) {
      this.handleEnemyKilled(target.x, target.y, this.scene.time.now);
    }
  }
}
